// Pre-rendered clip implementation of RoutineAnnouncer. Plays the Amazon Polly
// MP3s under /audio/<voiceId>/<clipId>.mp3 (generated by scripts/generate-audio.mjs),
// matching the Web Speech announcer's timing: pose name → pause → "Begin"
// (starts the clock) → countdown → release, etc.
//
// Playback goes through ONE shared Web Audio AudioContext, not an <audio>
// element, specifically to survive iOS audio-session interruptions. When a
// notification (or another app) grabs audio, iOS suspends the session; an
// <audio> element then refuses to resume without a fresh user tap — which is why
// cues used to go silent until you fiddled with the controls. An AudioContext,
// once unlocked by a gesture, CAN be resumed programmatically, so we:
//   (a) resume it before every cue (auto-recovers within ~10s during a hold),
//   (b) resume on visibilitychange / focus / the context's own statechange, and
//   (c) keep a silent keep-alive source running to hold the session warm.

import { BEGIN_PAUSE_MS, type RoutineAnnouncer } from './announcer';
import rawClipVoices from './clip-voices.json';

export interface ClipVoice {
  id: string;
  label: string;
  gender: string;
  accent: string;
}

export const CLIP_VOICES: ClipVoice[] = rawClipVoices as ClipVoice[];
export const hasClipVoice = (id: string): boolean => CLIP_VOICES.some((v) => v.id === id);
export const defaultClipVoiceId: string | null = CLIP_VOICES[0]?.id ?? null;
export const clipVoiceLabel = (id: string): string =>
  CLIP_VOICES.find((v) => v.id === id)?.label ?? id;

const clipUrl = (voiceId: string, clipId: string) => `/audio/${voiceId}/${clipId}.mp3`;

type Ctor = typeof AudioContext;
const getCtor = (): Ctor | null =>
  (typeof window !== 'undefined' &&
    (window.AudioContext ||
      (window as unknown as { webkitAudioContext?: Ctor }).webkitAudioContext)) ||
  null;

// --- Shared, interruption-resilient audio engine ---------------------------
let ctx: AudioContext | null = null;
let master: GainNode | null = null;
let keepAlive: AudioBufferSourceNode | null = null;
let recoveryBound = false;
const bufferCache = new Map<string, Promise<AudioBuffer | null>>();

function audioCtx(): AudioContext | null {
  if (ctx) return ctx;
  const Ctor = getCtor();
  if (!Ctor) return null;
  ctx = new Ctor();
  master = ctx.createGain();
  master.gain.value = 1;
  master.connect(ctx.destination);
  bindRecovery();
  return ctx;
}

/** Resume the context if the OS interrupted/suspended it, and keep it warm. */
function resumeAudio(): void {
  if (!ctx) return;
  if (ctx.state !== 'running') void ctx.resume().catch(() => {});
  startKeepAlive();
}

function bindRecovery(): void {
  if (recoveryBound || !ctx || typeof window === 'undefined') return;
  recoveryBound = true;
  // iOS 16.4+ flips state to 'interrupted'/'suspended' on an interruption;
  // resuming here (and when we regain focus/visibility) brings audio back with
  // no user tap.
  ctx.addEventListener?.('statechange', resumeAudio);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) resumeAudio();
  });
  window.addEventListener('focus', resumeAudio);
  window.addEventListener('pageshow', resumeAudio);
}

function startKeepAlive(): void {
  const c = audioCtx();
  if (!c || keepAlive) return;
  try {
    const buf = c.createBuffer(1, Math.max(1, Math.round(c.sampleRate * 0.5)), c.sampleRate);
    const src = c.createBufferSource();
    src.buffer = buf;
    src.loop = true;
    const g = c.createGain();
    g.gain.value = 0; // truly silent — just keeps the session alive
    src.connect(g).connect(c.destination);
    src.onended = () => {
      keepAlive = null;
    };
    src.start();
    keepAlive = src;
  } catch {
    /* best effort */
  }
}

/** Ensure the context exists and is running before we try to play a cue.
 *  The resume is bounded so a never-resolving resume() (e.g. an autoplay block)
 *  can't stall the cue — we proceed and let playback catch up. */
async function ensureRunning(): Promise<AudioContext | null> {
  const c = audioCtx();
  if (!c) return null;
  if (c.state !== 'running') {
    try {
      await Promise.race([c.resume(), new Promise((res) => setTimeout(res, 4000))]);
    } catch {
      /* ignore — the pre-cue resume is best effort */
    }
  }
  return c;
}

function loadBuffer(url: string): Promise<AudioBuffer | null> {
  const cached = bufferCache.get(url);
  if (cached) return cached;
  const p = (async () => {
    const c = audioCtx();
    if (!c) return null;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      const arr = await res.arrayBuffer();
      return await c.decodeAudioData(arr);
    } catch {
      return null;
    }
  })();
  bufferCache.set(url, p);
  return p;
}

/** Unlock the audio engine from within a user gesture (the Play/Begin tap). */
export function primeAudio(): void {
  if (typeof window === 'undefined') return;
  resumeAudio(); // creates + resumes the context and starts the keep-alive
}

/**
 * Clip announcer for one voice. Returns null when Web Audio is unavailable so
 * the caller falls back to the Web Speech announcer. Missing clips (a 404 or an
 * unmapped phrase) resolve as "ended" so the flow never hangs.
 */
export function createClipAnnouncer(voiceId: string): RoutineAnnouncer | null {
  if (typeof window === 'undefined' || !getCtor()) return null;

  let gen = 0;
  let current: AudioBufferSourceNode | null = null;
  let beginTimer: ReturnType<typeof setTimeout> | null = null;
  const clearBeginTimer = () => {
    if (beginTimer !== null) {
      clearTimeout(beginTimer);
      beginTimer = null;
    }
  };

  const stop = () => {
    if (current) {
      try {
        current.onended = null;
        current.stop();
      } catch {
        /* already stopped */
      }
      current = null;
    }
  };

  // Play one clip. `onStart` fires when audio begins (or immediately if the clip
  // is missing / the session can't be revived), `onEnd` when it finishes — so a
  // lost cue never strands the routine.
  const play = async (
    clipId: string,
    opts: { onStart?: () => void; onEnd?: () => void } = {},
  ) => {
    const myGen = gen;
    let ended = false;
    const finish = () => {
      if (ended || myGen !== gen) return;
      ended = true;
      opts.onEnd?.();
    };
    try {
      const c = await ensureRunning();
      if (myGen !== gen) return; // superseded while resuming
      const buf = await loadBuffer(clipUrl(voiceId, clipId));
      if (myGen !== gen) return;
      if (!c || !master || !buf) {
        // Missing/undecodable clip, or no audio: act as an instant, silent cue.
        opts.onStart?.();
        finish();
        return;
      }
      const src = c.createBufferSource();
      src.buffer = buf;
      src.connect(master);
      src.onended = finish;
      current = src;
      src.start();
      opts.onStart?.();
    } catch {
      opts.onStart?.();
      finish();
    }
  };

  return {
    unlock() {
      primeAudio();
      // Warm the always-used cues so the first one is instant.
      void loadBuffer(clipUrl(voiceId, 'begin'));
      void loadBuffer(clipUrl(voiceId, 'countdown'));
      void loadBuffer(clipUrl(voiceId, 'release'));
    },
    prefetch(clipId) {
      // Fetch + decode ahead of time so this clip's first play is instant.
      // decodeAudioData works on a suspended context, so this is safe to call
      // before any user gesture. Cached, so repeat calls are free.
      void loadBuffer(clipUrl(voiceId, clipId));
    },
    announceStep({ clipId }, onBegin, pauseMs) {
      gen++;
      stop();
      clearBeginTimer();
      const myGen = gen;
      const gap = pauseMs ?? BEGIN_PAUSE_MS;
      const scheduleBegin = () => {
        if (myGen !== gen) return;
        clearBeginTimer();
        beginTimer = setTimeout(() => {
          if (myGen !== gen) return;
          void play('begin', { onStart: () => onBegin?.() });
        }, gap);
      };
      if (clipId) void play(clipId, { onEnd: scheduleBegin });
      else scheduleBegin();
    },
    announceTimeRemaining(seconds) {
      void play(`remaining-${seconds}`);
    },
    announceCountdown() {
      void play('countdown');
    },
    announceRelease(onEnd) {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        onEnd?.();
      };
      void play('release', { onEnd: finish });
      setTimeout(finish, 2500); // fallback if the clip stalls
    },
    announceComplete() {
      gen++;
      stop();
      clearBeginTimer();
      void play('complete');
    },
    cancel() {
      gen++;
      stop();
      clearBeginTimer();
    },
  };
}
